import React, { useMemo, useRef, useEffect } from "react";
import { useAppointmentStatuses } from "../../hooks/useAppointmentStatuses";

/**
 * Alternating-side timeline. Mirrors the infographic style:
 *   - Horizontal line through the middle.
 *   - Markers (dots) sit on the line.
 *   - Each entry's date + snippet appears above OR below in alternating fashion.
 *   - Click a marker to select that session.
 *
 * Long histories scroll horizontally (overflow-x auto). Selected marker is
 * coloured primary; unselected markers are muted; cancelled / no-show
 * entries get a strikethrough on their label.
 */

const ENTRY_MIN_WIDTH = 170; // px
const TIMELINE_HEIGHT = 280; // px

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(d));
  } catch (_e) {
    return "—";
  }
}

function truncate(s, n = 110) {
  if (!s) return "";
  const trimmed = s.trim();
  if (trimmed.length <= n) return trimmed;
  return trimmed.slice(0, n - 1).trimEnd() + "…";
}

export default function HorizontalTimeline({
  timeline,
  selectedSessionId,
  onSelect,
  practiceId,
}) {
  const { behaviourOf } = useAppointmentStatuses(practiceId);

  // Struck through when the appointment did not happen. Driven by the status's
  // type, so a practice's own "Cancelled by Patient" is treated the same way.
  const isSkipped = (status) => behaviourOf(status, practiceId).strikeThrough;
  // Ascending order so earliest is on the left, newest on the right.
  const sorted = useMemo(() => {
    return [...(timeline || [])].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return ta - tb;
    });
  }, [timeline]);

  // Auto-scroll to the selected marker so users always see what's selected,
  // even when the timeline overflows horizontally.
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);
  useEffect(() => {
    if (selectedRef.current && scrollRef.current) {
      const cont = scrollRef.current;
      const el = selectedRef.current;
      const left = el.offsetLeft - cont.offsetWidth / 2 + el.offsetWidth / 2;
      cont.scrollTo({ left, behavior: "smooth" });
    }
  }, [selectedSessionId]);

  if (!sorted.length) {
    return (
      <div className="text-muted small py-3">No previous sessions yet.</div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="overflow-auto pb-2"
      style={{ scrollbarWidth: "thin" }}
    >
      <div
        className="position-relative"
        style={{
          height: TIMELINE_HEIGHT,
          minWidth: sorted.length * ENTRY_MIN_WIDTH,
        }}
      >
        {/* Horizontal spine */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 2,
            background: "#cbd5e0",
            transform: "translateY(-50%)",
          }}
        />

        {/* Markers + labels */}
        <div className="d-flex h-100" style={{ position: "relative" }}>
          {sorted.map((entry, i) => {
            const above = i % 2 === 0;
            const skipped = isSkipped(entry.status);
            const isSelected = entry.sessionId === selectedSessionId;
            const dotColor = isSelected
              ? "#0f3490"
              : skipped
                ? "#dc3545"
                : "#6c757d";

            const label = (
              <div
                className="px-2"
                style={{
                  textAlign: "center",
                  opacity: skipped ? 0.65 : 1,
                }}
              >
                <div
                  className="fw-semibold small"
                  style={{
                    color: isSelected ? "#0f3490" : "#212529",
                    textDecoration: skipped ? "line-through" : "none",
                  }}
                >
                  {formatDate(entry.date)}
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "0.75rem", lineHeight: 1.3 }}
                >
                  {entry.status && entry.status !== "Completed" && (
                    <span className="d-block">{entry.status}</span>
                  )}
                  {truncate(entry.summary)}
                </div>
              </div>
            );

            const connector = (
              <div
                style={{
                  width: 2,
                  height: 18,
                  background: dotColor,
                  margin: "0 auto",
                }}
              />
            );

            return (
              <div
                key={entry.sessionId}
                ref={isSelected ? selectedRef : null}
                className="flex-fill h-100 position-relative"
                style={{ minWidth: ENTRY_MIN_WIDTH }}
              >
                {/* Top half — content shows when `above` */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "calc(50% - 8px)",
                  }}
                  className="d-flex flex-column justify-content-end"
                >
                  {above && (
                    <>
                      {label}
                      {connector}
                    </>
                  )}
                </div>

                {/* Marker dot — sits on the horizontal spine */}
                <button
                  type="button"
                  onClick={() => onSelect && onSelect(entry)}
                  title={`${entry.status || "Session"} · ${formatDate(entry.date)}${entry.sourceType === "SYNCTO_NOTE" ? " · SyncTo" : ""}`}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: isSelected ? 18 : 14,
                    height: isSelected ? 18 : 14,
                    borderRadius: "50%",
                    background: dotColor,
                    border: "3px solid white",
                    boxShadow: "0 0 0 2px " + dotColor,
                    cursor: "pointer",
                    padding: 0,
                    zIndex: 2,
                    transition: "all 0.15s ease",
                  }}
                  aria-label={`Select session on ${formatDate(entry.date)}`}
                />

                {/* SyncTo badge under the dot */}
                {entry.sourceType === "SYNCTO_NOTE" && (
                  <i
                    className="fa-solid fa-microphone text-primary"
                    style={{
                      position: "absolute",
                      top: "calc(50% + 14px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: "0.65rem",
                      zIndex: 1,
                    }}
                    title="SyncTo-generated note"
                  />
                )}

                {/* Bottom half — content shows when `!above` */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "calc(50% - 8px)",
                  }}
                  className="d-flex flex-column justify-content-start"
                >
                  {!above && (
                    <>
                      {connector}
                      {label}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
